import { useAdminPlayOverview } from "@/features/play/api";
import { formatTime } from "@/features/play/format";

const TIME_LABEL = { now: "сейчас", evening: "вечером", any: "неважно", exact: "к времени" } as const;

/** Matchmaking overview: what people search for and what the matcher made of it. */
export function AdminPlayPage() {
  const { data, isLoading } = useAdminPlayOverview();

  return (
    <div>
      <header style={{ marginBottom: 16 }}>
        <h1 className="h1" style={{ margin: 0 }}>Подбор игр</h1>
        <p className="caption" style={{ color: "var(--fg2)", marginTop: 4 }}>
          Последние заявки «Найти соперника» и игры, которые из них собрались.
        </p>
      </header>

      {isLoading || !data ? (
        <div className="body">Загрузка…</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <Stat label="Ищут сейчас" value={data.searching} />
            <Stat label="Согласуют" value={data.negotiating} />
            <Stat label="Назначено" value={data.scheduled} />
          </div>

          <h2 className="h2" style={{ fontSize: 18 }}>Заявки</h2>
          <Table
            head={["#", "Игрок", "Спот", "Режим", "Когда", "Статус", "Создана"]}
            rows={data.requests.map((r) => [
              r.id,
              r.user,
              r.spot,
              r.mode === "rated" ? "рейтинг" : r.open_to_group ? "просто · компания" : "просто · 1×1",
              TIME_LABEL[r.time_mode],
              r.game_id ? `${r.status} → игра ${r.game_id}` : r.status,
              formatTime(r.created_at),
            ])}
          />

          <h2 className="h2" style={{ fontSize: 18, marginTop: 24 }}>Игры</h2>
          <Table
            head={["#", "Режим", "Статус", "Спот", "Время", "Участники"]}
            rows={data.games.map((g) => [
              g.id,
              g.mode === "rated" ? "рейтинг" : g.open_to_join ? "открытая" : "закрытая",
              g.status,
              g.spot ?? "—",
              g.scheduled_at ? formatTime(g.scheduled_at) : "—",
              g.players.join(", "),
            ])}
          />
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ background: "var(--bg-section)", borderRadius: "var(--r-lg)", padding: "12px 16px", minWidth: 120 }}>
      <div className="caption" style={{ color: "var(--fg2)" }}>{label}</div>
      <div className="h2" style={{ margin: 0 }}>{value}</div>
    </div>
  );
}

function Table({ head, rows }: { head: string[]; rows: (string | number)[][] }) {
  if (rows.length === 0) {
    return <div className="caption" style={{ color: "var(--fg3)" }}>Пока пусто.</div>;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            {head.map((h) => (
              <th
                key={h}
                style={{ textAlign: "left", padding: "6px 8px", color: "var(--fg2)", borderBottom: "1px solid var(--separator)" }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j} style={{ padding: "6px 8px", borderBottom: "1px solid var(--separator)", verticalAlign: "top" }}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
